import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, Eye, ChartLine } from '@phosphor-icons/react';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8" data-testid="stats-page">
        {/* Back Button */}
        <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">Statistiques</h1>
          <p className="text-muted-foreground">Suivez vos performances et votre croissance</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Eye size={20} weight="duotone" className="text-primary" />
              </div>
              <h3 className="font-semibold">Visibilité</h3>
            </div>
            <div className="text-3xl font-bold text-primary">{stats?.visibility_score || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Score actuel</p>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TrendUp size={20} weight="duotone" className="text-primary" />
              </div>
              <h3 className="font-semibold">Évolution</h3>
            </div>
            <div className="text-3xl font-bold text-green-500">+{stats?.score_evolution || 0}%</div>
            <p className="text-sm text-muted-foreground mt-2">Ce mois-ci</p>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ChartLine size={20} weight="duotone" className="text-primary" />
              </div>
              <h3 className="font-semibold">Contenu</h3>
            </div>
            <div className="text-3xl font-bold text-primary">{stats?.content_count || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Posts créés</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-xl p-6 mt-6"
        >
          <h2 className="text-lg font-semibold mb-4">Progression de visibilité</h2>
          <div className="h-64 flex items-end justify-between gap-2">
            {[45, 52, 48, 60, 58, 65, stats?.visibility_score || 70].map((value, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-gradient-to-t from-primary to-accent rounded-t transition-all duration-1000"
                  style={{ height: `${value}%` }}
                ></div>
                <span className="text-xs text-muted-foreground">S{i + 1}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </AppLayout>
  );
}
